// =============================================
// transferPlanBuilder — CZYSTA funkcja: domeny + konfiguracja + kontekst
// (zony ze wszystkich kont, cache NS) → plan per domena: 3 akcje
// (zone_create, ns_set, a_record) z decyzja run / skip + powodem.
//
// Zero sieci, zero React — testy 1:1 z regulami ponizej.
//
// Reguly (kolejnosc ma znaczenie):
//  1. alreadyCloudflare && existingZone      → 3x skip "juz na Cloudflare"
//  2. alreadyCloudflare && !existingZone     → 3x skip "CF spoza panelu" (nie tworzymy zony)
//  3. existingZone (NS nie CF)               → zone_create skip "zona istnieje na koncie X";
//                                              ns_set z existingZone.name_servers; a_record gdy IP
//  4. !existingZone && createZone.enabled    → wymaga konta z account_id; inaczej 3x skip
//  5. !createZone.enabled && !existingZone   → zone_create skip; ns_set tylko custom; a_record skip
//  6. ns_set: !enabled / not_on_account / NS nie sprawdzone / blad NS / custom < 2 / NS juz ustawione
//  7. a_record: !enabled / brak poprawnego IP
//  8. nsRewriteCount = liczba wierszy z ns_set run (liczba w dialogu)
// =============================================
import { isValidIPv4, type CfZoneWithAccount } from "@/services/cloudflareService";
import type {
  NsSkipCategory, PlanContext, PlanRow, PlannedAction, TransferConfig, TransferDomain, TransferPlan,
} from "./transferTypes";
import { normalizeDomain } from "@/services/aftermarketService";

const CF_NS_RE = /\.ns\.cloudflare\.com$/i;

/** Wszystkie NS (>= 1) wskazuja Cloudflare. */
export function isCloudflareNs(ns: string[]): boolean {
  const clean = ns.map((n) => n.trim().toLowerCase().replace(/\.$/, "")).filter(Boolean);
  return clean.length > 0 && clean.every((n) => CF_NS_RE.test(n));
}

/** Zbiory NS rowne (bez wielkosci liter, kolejnosci, kropki na koncu). */
export function sameNsSet(a: string[], b: string[]): boolean {
  const norm = (xs: string[]) => [...new Set(xs.map((n) => n.trim().toLowerCase().replace(/\.$/, "")).filter(Boolean))].sort();
  const na = norm(a); const nb = norm(b);
  return na.length === nb.length && na.every((x, i) => x === nb[i]);
}

/** Konto z account_id o najmniejszej liczbie zon (remis → pierwsze z listy). */
export function suggestLeastLoadedAccount(
  accounts: PlanContext["accounts"],
  zoneCountByAccount: Record<string, number>,
): string | null {
  let best: string | null = null;
  let bestCount = Infinity;
  for (const a of accounts) {
    if (!a.account_id?.trim()) continue;
    const c = zoneCountByAccount[a.id] ?? 0;
    if (c < bestCount) { best = a.id; bestCount = c; }
  }
  return best;
}

function zoneByDomain(zones: CfZoneWithAccount[]): Map<string, CfZoneWithAccount> {
  const m = new Map<string, CfZoneWithAccount>();
  for (const z of zones) {
    const k = normalizeDomain(z.name);
    if (k && !m.has(k)) m.set(k, z);
  }
  return m;
}

const skipNs = (reason: string, skipCategory: NsSkipCategory, manualNs?: string[] | "FROM_CREATED_ZONE"): PlannedAction =>
  ({ kind: "ns_set", decision: "skip", reason, skipCategory, ...(manualNs ? { manualNs } : {}) });

export function buildTransferPlan(domains: TransferDomain[], config: TransferConfig, ctx: PlanContext): TransferPlan {
  const zones = zoneByDomain(ctx.zones);
  const accountsById = new Map(ctx.accounts.map((a) => [a.id, a] as const));
  const rows: PlanRow[] = [];

  for (const d of domains) {
    const cache = ctx.nsCache[d.key];
    const currentNs = cache?.status === "ok" ? cache.ns : [];
    const alreadyCloudflare = cache?.status === "ok" && isCloudflareNs(cache.ns);
    const existingZone = zones.get(d.key) ?? null;

    // --- zone_create ---
    let zoneAction: PlannedAction;
    let createdZoneNsAvailable = false; // czy ns_set "from_zone" moze liczyc na NS z nowej zony
    let tokenCfAccountId: string | null = existingZone?.cfAccountId ?? null;

    if (alreadyCloudflare && existingZone) {
      zoneAction = { kind: "zone_create", decision: "skip", reason: "domena już na Cloudflare — hosting podepniesz w Domeny w CF", existingZone: { id: existingZone.id, cfAccountId: existingZone.cfAccountId, status: existingZone.status } };
    } else if (alreadyCloudflare && !existingZone) {
      zoneAction = { kind: "zone_create", decision: "skip", reason: "NS wskazują Cloudflare, ale zony nie ma w żadnym z naszych kont (inne konto CF?) — nie tworzymy" };
    } else if (existingZone) {
      zoneAction = { kind: "zone_create", decision: "skip", reason: `zona istnieje na koncie ${existingZone.cfAccountLabel}`, existingZone: { id: existingZone.id, cfAccountId: existingZone.cfAccountId, status: existingZone.status } };
    } else if (!config.createZone.enabled) {
      zoneAction = { kind: "zone_create", decision: "skip", reason: "tworzenie zony wyłączone" };
    } else {
      const accId = config.createZone.cfAccountId;
      const acc = accId ? accountsById.get(accId) : undefined;
      if (!acc) zoneAction = { kind: "zone_create", decision: "skip", reason: "nie wybrano konta Cloudflare" };
      else if (!acc.account_id?.trim()) zoneAction = { kind: "zone_create", decision: "skip", reason: `konto ${acc.label} bez Account ID — uzupełnij w Kontach` };
      else {
        zoneAction = { kind: "zone_create", decision: "run", cfAccountId: acc.id };
        createdZoneNsAvailable = true;
        tokenCfAccountId = acc.id;
      }
    }

    // --- ns_set ---
    let nsAction: PlannedAction;
    const targetNs: string[] | "FROM_CREATED_ZONE" | null =
      config.setNs.mode === "custom"
        ? config.setNs.customNs.map((n) => n.trim()).filter(Boolean)
        : existingZone
          ? (existingZone.name_servers?.length ? existingZone.name_servers : null)
          : createdZoneNsAvailable
            ? "FROM_CREATED_ZONE"
            : null;

    if (alreadyCloudflare && existingZone) {
      nsAction = skipNs("domena już na Cloudflare", "already_cf");
    } else if (alreadyCloudflare && !existingZone) {
      nsAction = skipNs("NS już wskazują Cloudflare (zona poza naszymi kontami)", "already_cf");
    } else if (!config.setNs.enabled) {
      nsAction = skipNs("zmiana NS wyłączona", "other", targetNs ?? undefined);
    } else if (config.setNs.mode === "custom" && (targetNs as string[]).length < 2) {
      nsAction = skipNs("podaj co najmniej 2 własne nameservery", "other");
    } else if (targetNs === null) {
      nsAction = existingZone
        ? skipNs("zona bez nameserverów — odśwież listę zon", "no_zone")
        : skipNs("brak zony (nie powstanie w tym planie) — brak NS do ustawienia", "no_zone");
    } else if (d.expirySource === "not_on_account") {
      nsAction = skipNs("domeny nie ma na koncie AfterMarket — ustaw NS ręcznie u rejestratora", "not_on_am", targetNs);
    } else if (!cache) {
      nsAction = skipNs("NS nie sprawdzone — kliknij „Sprawdź NS zaznaczonych”", "other", targetNs);
    } else if (cache.status === "error") {
      nsAction = skipNs(`AfterMarket nie zwrócił NS: ${cache.error} — ustaw ręcznie`, "not_on_am", targetNs);
    } else if (targetNs !== "FROM_CREATED_ZONE" && sameNsSet(currentNs, targetNs)) {
      nsAction = skipNs("NS już ustawione na docelowe", "other");
    } else {
      nsAction = { kind: "ns_set", decision: "run", workspace: d.workspace, ns: targetNs, currentNs };
    }

    // --- a_record ---
    let aAction: PlannedAction;
    const ip = (config.aRecord.ipOverrides[d.key] ?? d.hostingIp ?? "").trim();
    const zoneWillExist = !!existingZone || createdZoneNsAvailable;
    if (alreadyCloudflare && existingZone) {
      aAction = { kind: "a_record", decision: "skip", reason: "domena już na Cloudflare — hosting podepniesz w Domeny w CF" };
    } else if (alreadyCloudflare && !existingZone) {
      aAction = { kind: "a_record", decision: "skip", reason: "zona poza naszymi kontami" };
    } else if (!config.aRecord.enabled) {
      aAction = { kind: "a_record", decision: "skip", reason: "dodawanie rekordów A wyłączone" };
    } else if (!zoneWillExist) {
      aAction = { kind: "a_record", decision: "skip", reason: "brak zony — nie ma gdzie dodać rekordów" };
    } else if (!ip || !isValidIPv4(ip)) {
      aAction = { kind: "a_record", decision: "skip", reason: d.ipAmbiguous ? "kilka hostingów z różnymi IP — wybierz IP w kroku 2" : "brak IP hostingu — uzupełnij w kroku 2" };
    } else {
      aAction = { kind: "a_record", decision: "run", ip, proxied: config.aRecord.proxied };
    }

    rows.push({
      domain: d,
      alreadyCloudflare,
      existingZone,
      actions: [zoneAction, nsAction, aAction],
      willRewriteNs: nsAction.decision === "run",
      tokenCfAccountId,
    });
  }

  const skipSummary: Record<NsSkipCategory, number> = { already_cf: 0, not_on_am: 0, no_zone: 0, other: 0 };
  for (const r of rows) {
    const ns = r.actions[1];
    if (ns.kind === "ns_set" && ns.decision === "skip") skipSummary[ns.skipCategory]++;
  }

  return {
    rows,
    nsRewriteCount: rows.filter((r) => r.willRewriteNs).length,
    runnableCount: rows.filter((r) => r.actions.some((a) => a.decision === "run")).length,
    skipSummary,
  };
}
